import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpIcon from '@mui/icons-material/Help';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { TQuizAnswerWithQuestion } from '@onesource/services';
import { FC } from 'react';

type TAnswerReviewCardProps = {
  answer: TQuizAnswerWithQuestion;
  questionNumber: number;
  showCorrectAnswers: boolean;
};

/**
 * Answer Review Card Component
 */
export const AnswerReviewCard: FC<TAnswerReviewCardProps> = ({ answer, questionNumber, showCorrectAnswers }) => {
  const { question } = answer;
  const options = question.options || [];

  const getSelectedOptions = () => {
    if (answer.selectedOptionIds && answer.selectedOptionIds.length > 0) {
      return options.filter(opt => answer.selectedOptionIds.includes(opt.id)).map(opt => opt.optionText);
    }
    return [];
  };

  const getCorrectOptions = () => {
    return options.filter(opt => opt.isCorrect).map(opt => opt.optionText);
  };

  const getOrderingDisplay = (sequence: string[]) => {
    return sequence.map(id => options.find(opt => opt.id === id)?.optionText || 'Unknown').join(' → ');
  };

  const isCorrect = answer.isCorrect;
  const isPending = answer.isCorrect === null;

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: 'white',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '2px solid',
        borderColor: isPending ? '#ffa726' : isCorrect ? '#4caf50' : '#ef5350',
      }}
    >
      {/* Question Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box flex={1}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            <Box component="span" sx={{ color: '#ff4d6d' }}>
              Question {questionNumber}
            </Box>
          </Typography>
          <Typography variant="body1">{question.questionText}</Typography>
        </Box>
        <Box sx={{ ml: 2 }}>
          {isPending ? (
            <HelpIcon sx={{ color: '#ffa726', fontSize: 28 }} />
          ) : isCorrect ? (
            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28 }} />
          ) : (
            <CancelIcon sx={{ color: '#ef5350', fontSize: 28 }} />
          )}
        </Box>
      </Box>

      {/* Answer Details */}
      <Box sx={{ pl: 2 }}>
        {/* User's Answer */}
        <Box mb={1}>
          <Typography variant="body2" color="text.secondary" component="span">
            Your answer:{' '}
          </Typography>
          <Typography variant="body2" component="span" fontWeight={500}>
            {question.questionType === 'short_answer' && <>{answer.textAnswer || '(No answer)'}</>}
            {question.questionType === 'ordering' && answer.orderingSequence && (
              <>{getOrderingDisplay(answer.orderingSequence) || '(No answer)'}</>
            )}
            {['multiple_choice', 'multiple_select', 'true_false'].includes(question.questionType) && (
              <>{getSelectedOptions().join(', ') || '(No answer)'}</>
            )}
          </Typography>
        </Box>

        {/* Correct Answer */}
        {showCorrectAnswers && question.questionType !== 'short_answer' && (
          <Box mb={1}>
            <Typography variant="body2" color="text.secondary" component="span">
              Correct answer:{' '}
            </Typography>
            <Typography variant="body2" component="span" sx={{ color: '#4caf50', fontWeight: 500 }}>
              {question.questionType === 'ordering' ? (
                <>
                  {options
                    .sort((a, b) => a.position - b.position)
                    .map(opt => opt.optionText)
                    .join(' → ')}
                </>
              ) : (
                <>{getCorrectOptions().join(', ')}</>
              )}
            </Typography>
          </Box>
        )}

        {/* Explanation */}
        {showCorrectAnswers && question.explanation && (
          <Box
            mt={2}
            p={1.5}
            sx={{
              backgroundColor: 'rgba(255, 77, 109, 0.05)',
              borderRadius: 1,
              borderLeft: '3px solid #ff4d6d',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>Explanation:</strong> {question.explanation}
            </Typography>
          </Box>
        )}

        {/* Manual Grading Feedback */}
        {answer.feedback && (
          <Box
            mt={2}
            p={1.5}
            sx={{
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">
              <strong>Feedback:</strong> {answer.feedback}
            </Typography>
          </Box>
        )}

        {/* Pending Grading Notice */}
        {isPending && question.questionType === 'short_answer' && (
          <Alert severity="info" sx={{ mt: 1 }}>
            This answer requires manual grading and is pending review.
          </Alert>
        )}
      </Box>
    </Box>
  );
};
